import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import NoteLinkedEmbeddingHeader from 'notebook/embeddings/NoteLinkedEmbeddingHeader';
import {
  DEFAULT_NOTE_LINKED_EMBEDDING_VIEW_HEIGHT,
  EmbeddingProvenance,
  getNoteLinkedEmbeddingCid,
} from 'notebook/embeddings/NoteLinkedEmbeddingUtil';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import {BORDER_RADIUS_DEFAULT} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT} from 'ui-library/Colors';
import * as keyboard from 'util/Keyboard';
import {ElementClipboardEvent, ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

export interface ItemProps {}

export interface NoteLinkedEmbeddingComponentProps {
  children?: React.ReactNode;
  editor: NoteEditor;
  notePart: NotePart;
  editorProps: NoteEditorProps;
  index: number;
  itemProps: ItemProps;
  searchResultProps: MediocreSearchResultProps;
  contentId: string;
  name: string;
  provenance?: EmbeddingProvenance;
  getNameUpdate: (newName: string) => object;
}

export default function NoteLinkedEmbeddingComponent({
  children,
  editor,
  notePart,
  editorProps,
  index,
  itemProps,
  searchResultProps,
  contentId,
  name,
  provenance,
  getNameUpdate,
}: NoteLinkedEmbeddingComponentProps): JSX.Element {
  const handleDeletion = (): void => {
    editor.itemDeleteHandler(index);
  };

  const handleKeyDown = (e: ElementKeyboardEvent): void => {
    // Cut and copy will be handled by onCut / onCopy handlers (see below)
    if (keyboard.isCtrlX(e) || keyboard.isCtrlC(e)) {
      return;
    }

    // There are no other keyboard interactions that should use the browser default to manipulate the text in
    // the hidden contentEditable.
    e.preventDefault();

    if (keyboard.isUndoRedo(e)) {
      editorProps.restoreState(e, {selection: selutil.getSelectionIndices()!});
      return;
    }

    switch (e.which) {
      case 8:
      case 46: // BACKSPACE, DEL
        handleDeletion();
        break;
      case 13: // ENTER
        editor.insertItemHandler(e.shiftKey ? index - 1 : index, editor.defaultItemType());
        break;
      case 37: // LEFT
      case 38: // UP
        editor.moveHandler(index, {up: true, beginning: true});
        break;
      case 39: // RIGHT
      case 40: // DOWN
        editor.moveHandler(index, {up: false, beginning: true});
        break;
      default:
        break;
    }
  };

  const handleCopy = (e: ElementClipboardEvent): void => {
    e.preventDefault();
    editor.copyItemWithoutText(index, e);
  };

  const handleCut = (e: ElementClipboardEvent): void => {
    handleCopy(e);
    handleDeletion();
  };

  const handleItemClick = (e: ElementMouseEvent): void => {
    e.preventDefault();
    editorProps.requestSelection({
      selection: {cid: getNoteLinkedEmbeddingCid(editorProps.getCid(notePart)), start: 0, end: 0},
    });
  };

  const handleRename = (newName: string): void => {
    editor.updateNoteLinkedObject(index, getNameUpdate(newName));
  };

  const {worksheet} = WorksheetContext.useContext();
  const fileId = worksheet?.id ?? editor.props.fileId;
  const embeddingCid = getNoteLinkedEmbeddingCid(editorProps.getCid(notePart));

  return (
    <div
      className="note-linked-embedding"
      onMouseDown={handleItemClick}
      {...getTestProps({component: 'NoteLinkedEmbeddingComponent', element: 'root'})}
    >
      <div
        // We use a css focus selector on this hidden div to affect the styling of the caption editable.
        className="hiddenFocusEditable editable"
        {...getTestProps({
          component: 'NoteLinkedEmbeddingComponent',
          element: 'hiddenFocusEditable',
          key: embeddingCid,
        })}
        data-cid={embeddingCid}
        data-index={index}
        data-subindex="item"
        data-formattable={true}
        contentEditable={true}
        onCopy={handleCopy}
        onCut={editorProps.locked ? undefined : handleCut}
        onKeyDown={editorProps.locked ? undefined : handleKeyDown}
        {...getTestProps({component: 'NoteLinkedEmbeddingComponent', element: 'hiddenFocusEditable'})}
      />
      <div
        className={classNames(css(styles.noteLinkedEmbeddingContainer), 'note-linked-embedding-container')}
        {...getTestProps({component: 'NoteLinkedEmbeddingComponent', element: 'container'})}
      >
        <NoteLinkedEmbeddingHeader
          contentId={contentId}
          fileId={fileId}
          name={name}
          getEmbedding={() => children}
          onRename={editorProps.locked ? undefined : handleRename}
          provenance={provenance}
        />
        <div
          className={classNames(css(styles.noteLinkedEmbeddingView), 'note-linked-embedding-view')}
          {...getTestProps({component: 'NoteLinkedEmbeddingComponent', element: 'view'})}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  noteLinkedEmbeddingContainer: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: `${BORDER_RADIUS_DEFAULT}px`,
  },
  noteLinkedEmbeddingView: {height: `${DEFAULT_NOTE_LINKED_EMBEDDING_VIEW_HEIGHT}px`},
});
